import { AttachmentFile, notify } from '@billy/design-system'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { Origin } from '../../../enums/origin'
import { QueryKeys } from '../../../enums/queryKeys'
import { useSegment } from '../../../hooks'
import { useUploadFiles, UseUploadFilesProps } from './useUploadFiles'

export const useUploadAttachments = <TUploadResponse = AttachmentFile>({
  onError,
  onSuccess,
  onUpload,
  skipAutoForwardInfoModal,
}: UseUploadFilesProps<TUploadResponse> = {}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { track } = useSegment()

  return useUploadFiles<TUploadResponse>({
    onError: (error) => {
      notify({
        id: NotificationKeys.ReceiptsList,
        message: error?.body?.errorMessage || t('file.rejected'),
        variant: 'error',
      })

      onError?.(error)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(QueryKeys.AttachmentsCount)
      queryClient.invalidateQueries(QueryKeys.AttachmentsList)
      queryClient.invalidateQueries(QueryKeys.AttachmentsModal)

      notify({
        id: NotificationKeys.ReceiptsList,
        message: t('file.uploaded'),
        variant: 'success',
      })

      const hasDuplicate = data?.some((file) => (file as AttachmentFile | undefined)?.isDuplicate) ?? false
      if (hasDuplicate) {
        track(ExpensesTrackingEvents.DuplicateDetected)
        notify({
          id: NotificationKeys.BillAttachmentFileDuplicate,
          title: t('file.duplicate_warning.title'),
          message: t('file.duplicate_warning.message'),
          variant: 'warning',
        })
      }

      onSuccess?.(data)
    },
    onUpload,
    skipAutoForwardInfoModal,
    options: {
      fileOrigin: Origin.Web,
      'x-create-attachment': true,
      'x-create-variants': true,
      'x-should-scan': true,
    },
  })
}
